import { fetchAllBookings } from "../../api";
import { queryKeys } from "../../api";
import { getBookingTimeConstraint } from "../utils/bookingTimeConstraints.js";
import { generateBookingTitle } from "../utils/bookingTitleUtils.js";
import { useUrlFilterPersistence } from "../composables/useUrlFilterPersistence.js";
import { syncTimelineScrollers } from "../utils/scrollSyncUtils.js";
import { evaluateCalendarSelection } from "../utils/walkInSelection.js";
import { createOpeningHoursIndex } from "../utils/openingHours.js";

export function useMultiRoomConfig(
	queryClient,
	masterData,
	calendarFilters = null,
	handlers = null,
) {
	// Check if mobile/small screen
	const isMobile = window.innerWidth <= 768;

	// Get current user role for time constraints
	const userRole = masterData?.userRole?.value || null;
	const canViewOtherBookings = masterData?.canViewOtherBookings?.value || false;
	const earliestBookingSeconds =
		masterData?.earliestBookingSeconds?.value || 900;
	const canIgnoreOpeningHours =
		masterData?.canIgnoreOpeningHours?.value || false;

	// Track loading state to prevent empty state during loading
	let isLoading = false;

	const openingHoursIndex = createOpeningHoursIndex();

	// URL persistence for date
	const urlPersistence = useUrlFilterPersistence();
	const savedDate = urlPersistence.loadDateFromUrl();

	return {
		initialView: "resourceTimeline",
		initialDate: savedDate || undefined,
		resourcesInitiallyExpanded: true,
		headerToolbar: {
			left: "title",
			center: "",
			right: "prev,next,today,datePicker",
		},

		// Title format with weekday abbreviation (e.g., "Mo., 25. August 2025")
		titleFormat: {
			year: "numeric",
			month: "long",
			day: "numeric",
			weekday: "short",
		},

		// Mobile-responsive timeline settings
		slotMinWidth: 20,
		slotDuration: "00:15:00",
		slotLabelInterval: "01:00:00",

		eventMinWidth: 10,

		// Resource area width
		resourceAreaWidth: isMobile ? "100px" : "150px",

		// Mobile touch optimizations with dynamic scroll time
		scrollTime: (() => {
			const now = new Date();
			const currentHour = now.getHours();
			const currentMinute = now.getMinutes();

			return `${String(currentHour).padStart(2, "0")}:${String(
				currentMinute,
			).padStart(2, "0")}:00`;
		})(),
		scrollTimeReset: false,

		// Mobile-specific interaction improvements
		selectOverlap: function (event) {
			// Allow selection over background events (opening hours) but not over bookings
			return (
				event.display === "background" || event.display === "inverse-background"
			);
		},
		eventOverlap: function (stillEvent) {
			// Allow dragging over background events (opening hours) but not over other bookings
			return (
				stillEvent.display === "background" ||
				stillEvent.display === "inverse-background"
			);
		},

		// Enable selection for booking creation
		selectable: true,

		// Track loading state
		loading: function (isLoadingNow) {
			isLoading = isLoadingNow;
		},

		// Resource-specific selection: block past; Ad-Hoc only in the near window
		selectAllow: function (selectInfo) {
			if (!selectInfo.resource) return false;

			const roomId = parseInt(selectInfo.resource.id);
			const { allowed, isWalkIn } = evaluateCalendarSelection({
				selectStart: selectInfo.start,
				selectEnd: selectInfo.end,
				userRole,
				earliestBookingSeconds,
			});
			if (!allowed) return false;

			// Without the "ignore opening hours" permission a booking outside the opening
			// hours is impossible (walk-ins can never ignore them at all), so refuse the drag
			// instead of opening a modal that can only end in a server-side error
			if (
				!canIgnoreOpeningHours &&
				!openingHoursIndex.isRangeOpen(roomId, selectInfo.start, selectInfo.end)
			) {
				return false;
			}

			return isWalkIn
				? (masterData?.canCreateWalkInInRoom?.value(roomId) ?? false)
				: (masterData?.canCreateInRoom?.value(roomId) ?? false);
		},

		// Events for multi-room resource view
		events: async function (fetchInfo, successCallback, failureCallback) {
			try {
				// For resource timeline view, we'll use the current date
				const date = fetchInfo.end.toISOString().split("T")[0];

				// Fetch all bookings using queryClient
				const result = await queryClient.fetchQuery({
					queryKey: queryKeys.allBookings(date),
					queryFn: () => fetchAllBookings(date),
					staleTime: 1000 * 60 * 2, // 2 minutes
				});

				openingHoursIndex.reset();

				const bookings = Object.values(result.buildings).reduce(
					(acc, building) => {
						Object.values(building.rooms).forEach((room) => {
							const bookings = room.bookings.map((booking) => {
								// Apply time constraints only for students who actually have base permissions
								const timeConstraint =
									userRole === "student" &&
									(booking.permissions.canUpdate ||
										booking.permissions.canDelete)
										? getBookingTimeConstraint(booking, userRole)
										: { restricted: false };

								const effectiveCanEdit =
									booking.permissions.canUpdate && !timeConstraint.restricted;
								const effectiveCanDelete =
									booking.permissions.canDelete && !timeConstraint.restricted;

								// Add visual indicator classes for time-restricted bookings
								const classNames = booking.series ? ["series-event"] : [];
								if (timeConstraint.restricted && userRole === "student") {
									classNames.push("time-restricted-booking");
								}

								// Determine ownership by comparing user IDs
								const currentUserInfo = masterData?.masterData?.value?.userInfo;
								let isOwnBooking = false;

								if (currentUserInfo) {
									// Check employee ownership
									if (
										currentUserInfo.employee &&
										booking.employee &&
										currentUserInfo.employee.id === booking.employee.id
									) {
										isOwnBooking = true;
									}
									// Check student ownership
									if (
										currentUserInfo.student &&
										booking.student &&
										currentUserInfo.student.id === booking.student.id
									) {
										isOwnBooking = true;
									}
								}

								// Add ownership and permission classes
								if (isOwnBooking) {
									classNames.push("owned-booking");
									if (effectiveCanEdit) {
										classNames.push("can-edit");
									}
								}

								// Get booking type details from master data
								const bookingType = masterData?.getBookingTypeById?.value(
									booking.bookingTypeId,
								);

								// Permission to see booking details
								const canViewOtherBookings =
									masterData?.canViewOtherBookings.value;

								// Generate the title based on business requirements
								const displayTitle = generateBookingTitle(
									booking,
									bookingType,
									isOwnBooking || canViewOtherBookings,
								);

								return {
									id: booking.id.toString(),
									title: displayTitle,
									start: booking.startTime,
									end: booking.endTime,
									backgroundColor: bookingType?.backgroundColor || "#007bff",
									borderColor: bookingType?.backgroundColor || "#007bff",
									textColor: bookingType?.textColor || "#ffffff",
									classNames,
									editable: effectiveCanEdit,
									startEditable: effectiveCanEdit,
									durationEditable: effectiveCanEdit,
									resourceId: booking.roomId.toString(),
									extendedProps: {
										booking,
										// Original permissions
										originalCanEdit: booking.permissions.canUpdate,
										originalCanDelete: booking.permissions.canDelete,
										// Effective permissions with time constraints
										canEdit: effectiveCanEdit,
										canDelete: effectiveCanDelete,
										// Time constraint information
										timeConstraint,
										seriesId: booking.seriesId || null,
										// Add booking filter data
										bookingTypeId: booking.bookingTypeId,
										studentId: booking.student?.id,
										employeeId: booking.employee?.id,
									},
								};
							});

							acc.push(...bookings);
						});

						const openingHours = Object.values(building.openingHours).reduce(
							(acc, day) => {
								Object.values(building.rooms).forEach((room) => {
									day.sessions.forEach((session) => {
										openingHoursIndex.addSession(
											room.id,
											session.startTime,
											session.endTime,
										);
										acc.push({
											start: session.startTime,
											end: session.endTime,
											resourceId: room.id.toString(),
											display: "inverse-background",
											groupId: room.id.toString(),
											backgroundColor: "#cccccc",
										});
									});

									// if we have no sessions this would mean that the room is closed all day
									if (!day.sessions?.length) {
										openingHoursIndex.markLoaded(room.id);
										acc.push({
											start: new Date(date + "T00:00:00"),
											end: new Date(date + "T23:59:59"),
											resourceId: room.id.toString(),
											display: "background",
											groupId: room.id.toString(),
											backgroundColor: "#cccccc",
										});
									}
								});

								return acc;
							},
							[],
						);
						acc.push(...openingHours);

						return acc;
					},
					[],
				);

				// Apply booking filters if available and user is an employee
				let filteredBookings = bookings;
				if (calendarFilters && canViewOtherBookings) {
					// Separate booking events from opening hours events
					const bookingEvents = bookings.filter((event) => !event.display);
					const backgroundEvents = bookings.filter((event) => event.display);

					// Apply booking filters only to actual booking events
					const filteredBookingEvents =
						calendarFilters.applyBookingFilters(bookingEvents);

					// Combine filtered bookings with background events
					filteredBookings = [...filteredBookingEvents, ...backgroundEvents];
				}

				successCallback(filteredBookings);
			} catch (error) {
				console.error(
					"Failed to fetch calendar events for resource view:",
					error,
				);
				failureCallback(error);
			}
		},

		// Handle empty state for filtered results
		resourcesSet: function (resources) {
			// WebKit scroll sync fix: Safari has a race condition where header/body
			// scrollers desync during initial scrollTime application
			const calendarEl = document.querySelector(".fc");
			if (calendarEl) {
				requestAnimationFrame(() => {
					syncTimelineScrollers(calendarEl);
				});
			}

			// Handle empty state for filtered results
			if (resources.length === 0 && !isLoading) {
				const calendarEl = document.querySelector(".fc");
				if (
					calendarEl &&
					!calendarEl.querySelector(".booking-filter-empty-state")
				) {
					const emptyState = document.createElement("div");
					emptyState.className = "booking-filter-empty-state";
					emptyState.innerHTML = `
						<div style="
							text-align: center; 
							padding: 2rem; 
							color: #6c757d; 
							border: 1px dashed #dee2e6; 
							border-radius: 0.5rem; 
							margin: 1rem; 
							background-color: #f8f9fa;"
						>
							${
								!userRole || userRole === "none"
									? `<div style="font-size: 2rem; margin-bottom: 1rem;">🚫</div>
									<div style="font-weight: 500; margin-bottom: 0.5rem;">
										Es wurde kein Userprofil gefunden, welches Ihnen
										die Nutzung des Raumbuchungssystems erlaubt.
									</div>
									<div style="font-size: 0.9rem;">
										Bitte wenden Sie sich bei Fragen an den Administrator.
									</div>`
									: `<div style="font-size: 2rem; margin-bottom: 1rem;">🔍</div>
									<div style="font-weight: 500; margin-bottom: 0.5rem;">
										Keine Räume mit passenden Buchungen gefunden.
									</div>
									<div style="font-size: 0.9rem;">
										Bitte passen Sie die aktiven Filter an, um Ergebnisse zu sehen.
									</div>`
							}
						</div>
					`;
					calendarEl.appendChild(emptyState);
				}
				return;
			} else {
				// Remove empty state message if it exists
				const existingEmptyState = document.querySelector(
					".booking-filter-empty-state",
				);
				if (existingEmptyState) {
					existingEmptyState.remove();
				}
			}
		},

		// Save date changes to URL and refresh resources if booking filters are active
		datesSet: function (info) {
			// Call the original handler first (if provided)
			if (handlers?.handleDatesChange) {
				handlers.handleDatesChange(info);
			}

			// Use the calendar's currentDate or a calculated center date
			// For timeline views, info.start might be the beginning of visible range
			const calendar = info.view.calendar;
			const currentDate = calendar.getDate(); // This gets the "current" date FullCalendar is focused on

			if (currentDate) {
				urlPersistence.saveDateToUrl(currentDate);
			}

			// If booking filters are active, we need to refresh resources for the new date
			// because different rooms may have matching bookings on the new date
			if (calendarFilters && canViewOtherBookings) {
				const activeBookingFilters = calendarFilters.filterManager
					.getActiveFilters()
					.filter((filter) => filter.type === "booking");
				if (activeBookingFilters.length > 0) {
					// Use requestAnimationFrame to ensure the date change is fully processed
					requestAnimationFrame(() => {
						// Refresh resources for the new date
						calendar.refetchResources();
					});
				}
			}
		},
	};
}
